import { Button } from '@cottage/design-system/internal-tooling/ui'
import type {
  ExportConflictCode,
  ExportDryRunResponse,
  SheetLayoutDriftItem,
} from '@/services/projections'
import { monthLabel } from './monthColumns'

export type ExportConfirmDialogProps = {
  dryRun: ExportDryRunResponse
  lastClosedMonth: string
  exporting: boolean
  conflictCode: ExportConflictCode | null
  conflictDetail: string | null
  conflictDiff?: SheetLayoutDriftItem[] | null
  onCancel: () => void
  onConfirm: () => void
  onImport?: () => void
  onSave?: () => void
}

function firstProjectedMonthLabel(lastClosedMonth: string): string {
  return monthLabel(lastClosedMonth, 1)
}

export default function ExportConfirmDialog({
  dryRun,
  lastClosedMonth,
  exporting,
  conflictCode,
  conflictDetail,
  conflictDiff,
  onCancel,
  onConfirm,
  onImport,
  onSave,
}: ExportConfirmDialogProps) {
  const showImport =
    (conflictCode === 'stale_import' || conflictCode === 'schema_stale') && onImport != null
  const showSaveFirst = conflictCode === 'outputs_stale' && onSave != null
  const blockingConflict = conflictCode != null
  const conflictMessage =
    conflictCode === 'outputs_stale'
      ? 'Case was rolled forward to new actuals; save it before exporting.'
      : conflictDetail

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4"
      onClick={onCancel}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="export-confirm-title"
        className="relative flex max-h-[85vh] w-full max-w-lg flex-col rounded-xl bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="border-b border-gray-200 px-5 py-4">
          <h3 id="export-confirm-title" className="text-sm font-semibold text-gray-900">
            Export to CGM
          </h3>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
          {conflictMessage ? (
            <p className="mb-4 text-sm text-red-600">{conflictMessage}</p>
          ) : null}
          {conflictCode === 'sheet_layout_drift' && conflictDiff && conflictDiff.length > 0 ? (
            <ul className="mb-4 list-disc space-y-1 pl-5 text-sm text-red-600">
              {conflictDiff.map((item, index) => (
                <li key={`drift-${index}`}>
                  {item.tab ?? '—'} / {item.row ?? '—'} / {item.expected ?? '—'} /{' '}
                  {item.actual ?? '—'}
                </li>
              ))}
            </ul>
          ) : null}
          <ul className="space-y-3 text-sm text-gray-700">
            {dryRun.ranges.map((range) => (
              <li key={`${range.tab}-${range.range}`}>
                <p className="font-medium text-gray-900">{range.tab}</p>
                <p className="text-gray-600">Range: {range.range}</p>
                <p className="text-gray-600">
                  First projected month: {firstProjectedMonthLabel(lastClosedMonth)}
                </p>
                <p className="text-gray-600">First column: {dryRun.first_col ?? range.first_col}</p>
              </li>
            ))}
          </ul>
          {dryRun.non_empty.length > 0 ? (
            <div className="mt-4">
              <p className="text-sm font-semibold text-gray-900">
                Warning: non-empty cells in target ranges
              </p>
              <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-gray-700">
                {dryRun.non_empty.map((entry) => (
                  <li key={`${entry.tab}-${entry.range}`}>
                    {entry.tab} {entry.range}: {entry.cells.length} cell
                    {entry.cells.length === 1 ? '' : 's'}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </div>
        <div className="flex flex-wrap justify-end gap-2 border-t border-gray-200 px-5 py-4">
          {showImport ? (
            <Button variant="outline" size="md" onClick={onImport}>
              Update Historicals
            </Button>
          ) : null}
          {showSaveFirst ? (
            <Button variant="outline" size="md" onClick={onSave}>
              Save
            </Button>
          ) : null}
          <Button variant="outline" size="md" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="md"
            disabled={exporting || blockingConflict}
            onClick={onConfirm}
          >
            Export
          </Button>
        </div>
      </div>
    </div>
  )
}
